import React, { useState, useEffect, useMemo } from "react"
import { getRuntimeEnv } from "@utils/env"
import {
	Box,
	Title,
	Text,
	Button,
	Group,
	TextInput,
	Checkbox,
	Stack,
	Flex
} from "@mantine/core"
import styles from "./MFAModal.module.css"
import {
	AEM_DICTIONARY_COLLECTIONS,
	useAEMDictionary
} from "@AEM/hooks/useAEMDictionary"
import { stylizeText } from "@utils/stylizeText"
import {
	parseForgeRockErrorMessage,
	type MFATextOutputConfig
} from "@utils/forgeRock.util"
import { AEMImage } from "@components/AEMImage"
import { useAEMIcons } from "@AEM/hooks/useAEMIcons"
import { AppModal } from "@components/ui/AppModal/AppModal"
import { useAdminContactEmail } from "@services/adminContactService"

interface MFAModalProps {
	opened: boolean
	username?: string
	onClose: () => void
	onSubmit: (otp: string, rememberDevice: boolean) => Promise<void>
	onSuccess: () => void
	onResendOTP?: () => Promise<void>
	onSendSMS?: () => Promise<void>
	isLoading: boolean
	error: string
	mfaConfig?: MFATextOutputConfig
}

export const MFAModal: React.FC<MFAModalProps> = ({
	opened,
	username,
	onClose,
	onSubmit,
	onSuccess: _onSuccess, // Success is handled by parent component
	onResendOTP,
	onSendSMS,
	isLoading = false,
	error,
	mfaConfig
}) => {
	const { dictionary } = useAEMDictionary(
		AEM_DICTIONARY_COLLECTIONS.REGISTRATION
	)
	const { icons } = useAEMIcons()
	const timeoutIcon = icons["timeout.svg"]
	const retryIcon = icons["re-try.svg"]

	const [otp, setOtp] = useState<string[]>(Array(6).fill(""))
	const [hasError, setHasError] = useState(false)
	const [isValidating, setIsValidating] = useState(false)
	const [isResending, setIsResending] = useState(false)
	const [isSendingSMS, setIsSendingSMS] = useState(false)
	const [errorMessage, setErrorMessage] = useState("")
	const defaultRememberDevice =
		getRuntimeEnv("VITE_MFA_REMEMBER_DEVICE_DEFAULT") === "true"
	const [rememberDevice, setRememberDevice] = useState(defaultRememberDevice)
	const [secondsRemaining, setSecondsRemaining] = useState(0)
	const { data: adminEmail } = useAdminContactEmail(username, opened)

	// Check if error is expired code
	const isExpiredError = useMemo(() => {
		if (!error && !errorMessage) return false
		const errorText = error || errorMessage
		const parsed = parseForgeRockErrorMessage(errorText)
		return (
			parsed.errorType === "EXPIRED_OTP" ||
			errorText.toLowerCase().includes("expired") ||
			errorText.toLowerCase().includes("timeout")
		)
	}, [error, errorMessage])

	// Check if error is max OTP attempts (LoginFailure with INVALID_OTP)
	const isMaxAttemptsError = useMemo(() => {
		if (!error && !errorMessage) return false
		const errorText = error || errorMessage
		const parsed = parseForgeRockErrorMessage(errorText)
		return parsed.errorType === "INVALID_OTP"
	}, [error, errorMessage])

	const waitTimeSeconds = (mfaConfig?.timelapse ?? 1) * 60

	// Timer countdown effect
	useEffect(() => {
		if (secondsRemaining <= 0) return

		const intervalId = setInterval(() => {
			setSecondsRemaining((prev) => prev - 1)
		}, 1000)

		return () => clearInterval(intervalId)
	}, [secondsRemaining])

	// Reset all states when modal opens or closes
	useEffect(() => {
		const resetState = () => {
			setOtp(Array(6).fill(""))
			setHasError(false)
			setErrorMessage("")
			setIsValidating(false)
			setIsResending(false)
			setIsSendingSMS(false)
			setRememberDevice(defaultRememberDevice)
		}

		if (opened) {
			resetState()
			// Start timer immediately as first OTP is sent before modal opens
			setSecondsRemaining(waitTimeSeconds)

			// Focus first input
			setTimeout(() => {
				const firstInput = document.getElementById("otp-input-0")
				firstInput?.focus()
			}, 100)
		} else {
			// Clean up on close
			resetState()
			setSecondsRemaining(0)
		}
	}, [opened])

	// Update error message when error prop changes
	useEffect(() => {
		if (error) {
			setHasError(true)
			setErrorMessage(error)
		}
	}, [error])

	const handleKeyDown = (
		index: number,
		event: React.KeyboardEvent<HTMLInputElement>
	) => {
		if (isLoading) return

		const newOtp = [...otp]

		if (event.key === "Backspace") {
			event.preventDefault()
			if (newOtp[index]) {
				// If current input has a value, clear it
				newOtp[index] = ""
				setOtp(newOtp)
			} else if (index > 0) {
				// If current input is empty, move to previous and clear it
				newOtp[index - 1] = ""
				setOtp(newOtp)
				const prevInput = document.getElementById(`otp-input-${index - 1}`)
				prevInput?.focus()
			}
		} else if (event.key === "Delete") {
			event.preventDefault()
			newOtp[index] = ""
			setOtp(newOtp)
		} else if (/^[0-9]$/.test(event.key)) {
			event.preventDefault()
			newOtp[index] = event.key
			setOtp(newOtp)

			// Auto-focus next input if not the last one
			if (index < 5) {
				setTimeout(() => {
					const nextInput = document.getElementById(`otp-input-${index + 1}`)
					nextInput?.focus()
				}, 0)
			}
		} else if (event.key === "ArrowLeft" && index > 0) {
			event.preventDefault()
			const prevInput = document.getElementById(`otp-input-${index - 1}`)
			prevInput?.focus()
		} else if (event.key === "ArrowRight" && index < 5) {
			event.preventDefault()
			const nextInput = document.getElementById(`otp-input-${index + 1}`)
			nextInput?.focus()
		}
	}

	const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
		if (isLoading) return

		event.preventDefault()
		const pastedData = event.clipboardData.getData("text")

		// Extract all digits from pasted content (handles multi-line, spaces, etc.)
		// Example: "8\n\n5\n\n8\n\n1\n\n4\n\n2" -> "858142"
		const digits = pastedData.replace(/\D/g, "").slice(0, 6)

		if (digits.length === 0) return

		const newOtp = [...otp]

		// Fill inputs with extracted digits
		for (let i = 0; i < 6; i++) {
			if (i < digits.length) {
				newOtp[i] = digits[i]
			}
		}

		setOtp(newOtp)

		// Focus the next empty input or the last input
		setTimeout(() => {
			const nextEmptyIndex = newOtp.findIndex(
				(digit, idx) => digit === "" && idx < 6
			)
			const targetIndex = nextEmptyIndex >= 0 ? nextEmptyIndex : 5
			const nextInput = document.getElementById(`otp-input-${targetIndex}`)
			nextInput?.focus()
		}, 0)
	}

	const handleChange = (
		index: number,
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		if (isLoading) return

		// Clear errors when user starts typing
		if (hasError) {
			setHasError(false)
			setErrorMessage("")
		}

		const value = event.target.value

		// Handle multiple characters (e.g., from paste or autocomplete)
		if (value.length > 1) {
			// Extract only digits
			const digits = value.replace(/\D/g, "").slice(0, 6)
			const newOtp = [...otp]

			// Fill inputs starting from current index
			for (let i = 0; i < digits.length && index + i < 6; i++) {
				newOtp[index + i] = digits[i]
			}

			setOtp(newOtp)

			// Focus the next empty input or the last input
			setTimeout(() => {
				const nextEmptyIndex = newOtp.findIndex(
					(digit, idx) => digit === "" && idx < 6
				)
				const targetIndex = nextEmptyIndex >= 0 ? nextEmptyIndex : 5
				const nextInput = document.getElementById(`otp-input-${targetIndex}`)
				nextInput?.focus()
			}, 0)
			return
		}

		// Handle single character input
		if (value === "" || /^[0-9]$/.test(value)) {
			const newOtp = [...otp]
			newOtp[index] = value
			setOtp(newOtp)

			// Auto-focus next input if a digit was entered
			if (value && index < 5) {
				setTimeout(() => {
					const nextInput = document.getElementById(`otp-input-${index + 1}`)
					nextInput?.focus()
				}, 0)
			}
		}
	}

	const handleValidateCode = async () => {
		// Validate that all 6 digits are filled
		const otpCode = otp.join("")
		if (otpCode.length !== 6) {
			setHasError(true)
			setErrorMessage("Please enter all 6 digits of the code")
			return
		}

		setIsValidating(true)
		setHasError(false)
		setErrorMessage("")

		try {
			// Submit the OTP through the PasswordCallback
			await onSubmit(otpCode, rememberDevice)
			setIsValidating(false)
			// Success is handled by the parent component via onSuccess callback
		} catch (error) {
			setIsValidating(false)
			setHasError(true)
			// Use error message from the submission if available, otherwise use default
			const errorMsg =
				error instanceof Error
					? error.message
					: dictionary?.mfaOTPInvalidError || ""
			setErrorMessage(errorMsg)
		}
	}

	const handleErrorModalClose = async () => {
		if (!onResendOTP) {
			setHasError(false)
			setErrorMessage("")
			setOtp(Array(6).fill(""))
			onClose()
			return
		}

		setIsResending(true)
		try {
			// Clear error state first
			setHasError(false)
			setErrorMessage("")
			setOtp(Array(6).fill(""))

			// Call resend OTP function
			await onResendOTP()
			setSecondsRemaining(waitTimeSeconds)
			// After resending, the parent component will update the step
			// and the modal will automatically show the normal OTP input view
			setIsResending(false)
		} catch (error) {
			setIsResending(false)
			// If resend fails, show error but keep modal open
			setHasError(true)
			const errorMsg =
				error instanceof Error
					? error.message
					: "Failed to generate new code. Please try again."
			setErrorMessage(errorMsg)
		}
	}

	const handleResendOTP = async () => {
		if (!onResendOTP) return

		// Don't allow resend if still in wait period
		if (secondsRemaining > 0) return

		setIsResending(true)
		setHasError(false)
		setErrorMessage("")
		setOtp(Array(6).fill(""))

		try {
			await onResendOTP()
			setSecondsRemaining(waitTimeSeconds)
			setIsResending(false)
		} catch (error) {
			setIsResending(false)
			setHasError(true)
			const errorMsg =
				error instanceof Error
					? error.message
					: "Failed to generate new code. Please try again."
			setErrorMessage(errorMsg)
		}
	}

	const handleSendSMS = async () => {
		if (!onSendSMS) return

		setIsSendingSMS(true)
		setHasError(false)
		setErrorMessage("")
		setOtp(Array(6).fill(""))

		try {
			await onSendSMS()
			setIsSendingSMS(false)
		} catch (error) {
			setIsSendingSMS(false)
			setHasError(true)
			const errorMsg =
				error instanceof Error
					? error.message
					: "Failed to send SMS. Please try again."
			setErrorMessage(errorMsg)
		}
	}

	return (
		<AppModal
			opened={opened}
			onClose={onClose}
			size={760}
			title={dictionary?.mfaTitle || ""}
			hasScrollableBody={false}
			closeOnClickOutside={false}
			p={{ base: "16px 20px 0px 20px", sm: "30px 40px 0px 40px" }}
		>
			{isExpiredError || isMaxAttemptsError ? (
				<Stack gap="md" align="center" py="xl">
					{timeoutIcon && retryIcon && (
						<Box>
							<AEMImage
								imageData={isMaxAttemptsError ? retryIcon : timeoutIcon}
								w={140}
								h={140}
							/>
						</Box>
					)}
					<Stack gap={6} align="center">
						<Title variant="title-gradient" ta="center">
							{isMaxAttemptsError
								? dictionary?.mfaOTPInvalidError
								: dictionary?.mfaCodeExpired}
						</Title>
						<Text ta="center">
							{isMaxAttemptsError
								? dictionary?.mfaOTPMaxReached
								: (dictionary?.mfaTimeoutIntro?.replace(
										"{}",
										(mfaConfig?.otpExpiry ?? "").toString()
									) ?? "")}
						</Text>
						{isMaxAttemptsError && mfaConfig?.secondaryMfa !== "none" && (
							<Text ta="center">{dictionary?.mfaOTPBackupMethod}</Text>
						)}
					</Stack>
					<Button
						variant="dark-blue"
						size="lg"
						mt="xl"
						onClick={isMaxAttemptsError ? onClose : handleErrorModalClose}
						loading={!isMaxAttemptsError && (isResending || isLoading)}
						disabled={!isMaxAttemptsError && (isResending || isLoading)}
					>
						{isMaxAttemptsError
							? (dictionary?.mfaClose ?? "Close")
							: (dictionary?.mfaTimeoutSubmitButton ?? "")}
					</Button>
				</Stack>
			) : (
				<>
					<Stack gap="xs" mb="xl">
						<Title fz={24}>{dictionary?.mfaNewDeviceLabel}</Title>
						<Text>{dictionary?.mfaNewDeviceIntro1}</Text>
						<Text>{dictionary?.mfaNewDeviceIntro2}</Text>
					</Stack>
					<Stack>
						<Text fw={600}>{dictionary?.mfaEnterCodeLabel}</Text>
						<Flex
							direction={{ base: "column", sm: "row" }}
							justify="space-between"
							align="center"
							gap="md"
						>
							<Group gap="xs" className={styles.otpInputContainer}>
								{otp.map((digit, index) => (
									<TextInput
										key={index}
										id={`otp-input-${index}`}
										maxLength={1}
										value={digit}
										disabled={isLoading}
										onChange={(e) => handleChange(index, e)}
										onKeyDown={(e) => handleKeyDown(index, e)}
										onPaste={(e) => handlePaste(e)}
										onFocus={(e) => e.target.select()}
										autoComplete="off"
										inputMode="numeric"
										pattern="[0-9]*"
										className={styles.otpInput}
									/>
								))}
							</Group>

							<Button
								size="lg"
								variant="dark-blue"
								onClick={handleValidateCode}
								loading={isValidating || isLoading}
								disabled={
									otp.some((digit) => digit === "") || isValidating || isLoading
								}
								className={styles.validateButton}
							>
								{dictionary?.mfaCodeSubmitButton}
							</Button>
						</Flex>
						{(hasError || error) && !isExpiredError && (
							<Text size="sm" c="red">
								{errorMessage || error || dictionary?.mfaOTPInvalidError}
							</Text>
						)}
						{(isResending || isSendingSMS) && (
							<Text size="sm">
								{isResending ? "Generating new code..." : "Sending SMS code..."}
							</Text>
						)}
					</Stack>
					<Checkbox
						checked={rememberDevice}
						onChange={(event) => setRememberDevice(event.currentTarget.checked)}
						label={dictionary?.mfaNewDeviceValidDuration?.replace(
							"{}",
							(mfaConfig?.deviceDays ?? "").toString()
						)}
						size="md"
						fz="14px"
						my={40}
					/>

					<Stack
						bg="var(--mantine-color-royal-10)"
						mx={{ base: -20, sm: -40 }}
						mt="xl"
						p={{ base: "20px", sm: "24px 40px" }}
					>
						<Text>
							{dictionary?.mfaCodeExpiryLabel?.replace(
								"{}",
								(mfaConfig?.otpExpiry ?? "").toString()
							)}
						</Text>

						<Group gap="sm" align="center" wrap="wrap">
							<Button
								variant="dark-blue"
								size="md"
								onClick={handleResendOTP}
								disabled={isResending || isLoading || secondsRemaining > 0}
								loading={isResending}
							>
								{dictionary?.mfaTimeoutSubmitButton}
							</Button>

							{secondsRemaining > 0 && (
								<Text>
									{dictionary?.mfaWaitMinutesLabel?.replace(
										"{}",
										(mfaConfig?.timelapse ?? "").toString()
									)}
								</Text>
							)}
						</Group>

						{mfaConfig?.secondaryMfa !== "none" && (
							<Text>
								{stylizeText(dictionary?.mfaSecondaryLabel || "", {
									onAnchorClick: handleSendSMS,
									linkFw: 600
								})}
							</Text>
						)}
						{adminEmail && (
							<Text>
								{stylizeText(
									(dictionary?.mfaContactAdmin || "").replace(
										"{}",
										`[${adminEmail}]`
									),
									{
										linkFw: 600,
										onAnchorClick: () => window.open(`mailto:${adminEmail}`)
									}
								)}
							</Text>
						)}
					</Stack>
				</>
			)}
		</AppModal>
	)
}
